import { useEffect, useState } from "react";
import { Shield, UserPlus, Trash2, Save, X, KeyRound, History, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { readError } from "../api";

const ACTION_LABEL = {
  "auth.login": { label: "Giriş", color: "#0ea5e9" },
  "user.create": { label: "Kullanıcı ekle", color: "#059669" },
  "user.update": { label: "Kullanıcı güncelle", color: "#d97706" },
  "user.delete": { label: "Kullanıcı sil", color: "#dc2626" },
  "settings.update": { label: "Ayar güncelle", color: "#7c3aed" },
  "setup.init": { label: "İlk kurulum", color: "#2563eb" },
};

export default function AdminPanel({ me }) {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [audit, setAudit] = useState([]);
  const [auditFilter, setAuditFilter] = useState("");
  const [auditBusy, setAuditBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (err) { toast.error(readError(err)); }
  };
  const loadAudit = async () => {
    setAuditBusy(true);
    try {
      const { data } = await api.get("/audit-logs", { params: auditFilter ? { action: auditFilter } : {} });
      setAudit(data);
    } catch (err) { toast.error(readError(err)); }
    finally { setAuditBusy(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "audit") loadAudit(); /* eslint-disable-next-line */ }, [tab, auditFilter]);

  const createUser = async () => {
    if (!creating.email || !creating.password) return toast.error("E-posta ve şifre zorunlu");
    try {
      await api.post("/users", creating);
      toast.success("Kullanıcı oluşturuldu");
      setCreating(null); load();
    } catch (err) { toast.error(readError(err)); }
  };
  const removeUser = async (id) => {
    if (!window.confirm("Kullanıcı silinsin mi?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(readError(err)); }
  };
  const resetPass = async () => {
    if (!resetting.password || resetting.password.length < 6) return toast.error("En az 6 karakter");
    try {
      await api.patch(`/users/${resetting.id}`, { password: resetting.password });
      toast.success("Şifre değiştirildi");
      setResetting(null);
    } catch (err) { toast.error(readError(err)); }
  };
  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); load(); toast.success("Rol güncellendi"); }
    catch (err) { toast.error(readError(err)); }
  };

  return (
    <section className="admin-view">
      <div className="catalog-intro">
        <div className="settings-icon"><Shield size={22} /></div>
        <div>
          <p className="eyebrow blue">YÖNETİM PANELİ</p>
          <h2>Kullanıcılar ve Denetim</h2>
          <p>Sistem kullanıcılarını yönetin ve son değişiklikleri denetleyin.</p>
        </div>
      </div>

      <div className="admin-tabs">
        <button data-testid="tab-users" className={`admin-tab ${tab === "users" ? "active" : ""}`}
          onClick={() => setTab("users")}><Shield size={14} /> Kullanıcılar</button>
        <button data-testid="tab-audit" className={`admin-tab ${tab === "audit" ? "active" : ""}`}
          onClick={() => setTab("audit")}><History size={14} /> Denetim kaydı</button>
      </div>

      {tab === "users" && (
        <>
          <div className="catalog-actions">
            <button data-testid="new-user-button" className="primary-button"
              onClick={() => setCreating({ email: "", password: "", name: "", role: "user" })}>
              <UserPlus size={16} /> Yeni kullanıcı
            </button>
          </div>
          <div className="catalog-list">
            <div className="user-row user-head">
              <span>Ad</span><span>E-posta</span><span>Rol</span><span>Oluşturma</span><span></span>
            </div>
            {users.map(u => (
              <div className="user-row" key={u.id} data-testid={`user-row-${u.email}`}>
                <span>{u.name || "-"}</span>
                <span>{u.email}</span>
                <select value={u.role || "user"} onChange={e => changeRole(u, e.target.value)}
                  data-testid={`user-role-${u.email}`} disabled={u.id === me.id}>
                  <option value="admin">Admin</option>
                  <option value="user">Kullanıcı</option>
                </select>
                <span>{u.created_at ? new Date(u.created_at).toLocaleDateString("tr-TR") : "-"}</span>
                <div className="customer-actions">
                  <button className="icon-button" title="Şifreyi değiştir"
                    onClick={() => setResetting({ id: u.id, password: "" })}
                    data-testid={`reset-user-${u.email}`}><KeyRound size={13} /></button>
                  <button className="icon-button" title="Sil" onClick={() => removeUser(u.id)}
                    disabled={u.id === me.id} data-testid={`delete-user-${u.email}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "audit" && (
        <div className="audit-panel" data-testid="audit-panel">
          <div className="catalog-actions" style={{ gap: 10 }}>
            <select data-testid="audit-filter" className="status-filter"
              value={auditFilter} onChange={e => setAuditFilter(e.target.value)}>
              <option value="">Tüm hareketler</option>
              {Object.entries(ACTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button className="outline-button" onClick={loadAudit} disabled={auditBusy} data-testid="audit-refresh">
              <RefreshCw size={14} /> {auditBusy ? "Yenileniyor..." : "Yenile"}
            </button>
          </div>
          <div className="audit-list">
            {audit.length === 0 && (
              <div className="empty-state" style={{ padding: 40 }}>
                <History size={26} />
                <h2>Kayıt yok</h2>
                <p>Kullanıcı ve ayar değişiklikleri burada listelenir.</p>
              </div>
            )}
            {audit.map((row, i) => {
              const meta = ACTION_LABEL[row.action] || { label: row.action, color: "#64748b" };
              const dt = row.created_at ? new Date(row.created_at) : null;
              return (
                <div className="audit-row" key={i} data-testid={`audit-row-${i}`}>
                  <div className="audit-time">
                    <b>{dt ? dt.toLocaleDateString("tr-TR") : "-"}</b>
                    <span>{dt ? dt.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                  </div>
                  <span className="audit-badge" style={{ background: `${meta.color}20`, color: meta.color }}>
                    {meta.label}
                  </span>
                  <div className="audit-actor">
                    <b>{row.actor_email || "sistem"}</b>
                    {row.ip && <span>IP {row.ip}</span>}
                  </div>
                  <div className="audit-details">
                    {row.entity_type && <span className="audit-entity">{row.entity_type}</span>}
                    {row.details && Object.keys(row.details).length > 0 && (
                      <code>{JSON.stringify(row.details)}</code>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {creating && (
        <div className="modal-overlay" onClick={() => setCreating(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} data-testid="user-create-modal">
            <button className="modal-close" onClick={() => setCreating(null)}><X size={16} /></button>
            <h2>Yeni kullanıcı</h2>
            <div className="settings-grid">
              <label>Ad<input data-testid="new-user-name" value={creating.name} onChange={e => setCreating({ ...creating, name: e.target.value })} /></label>
              <label>Rol
                <select data-testid="new-user-role" value={creating.role} onChange={e => setCreating({ ...creating, role: e.target.value })}>
                  <option value="user">Kullanıcı</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
              <label className="full-field">E-posta<input data-testid="new-user-email" type="email" value={creating.email} onChange={e => setCreating({ ...creating, email: e.target.value })} /></label>
              <label className="full-field">Şifre (min 6 karakter)<input data-testid="new-user-password" type="text" value={creating.password} onChange={e => setCreating({ ...creating, password: e.target.value })} /></label>
            </div>
            <div className="settings-actions">
              <button className="outline-button" onClick={() => setCreating(null)}>Vazgeç</button>
              <button className="primary-button" onClick={createUser} data-testid="new-user-submit">
                <Save size={16} /> Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {resetting && (
        <div className="modal-overlay" onClick={() => setResetting(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} data-testid="user-reset-modal">
            <button className="modal-close" onClick={() => setResetting(null)}><X size={16} /></button>
            <h2>Şifre değiştir</h2>
            <p>Yeni şifreyi girin, kullanıcıya iletin.</p>
            <input data-testid="reset-pass-input" type="text" placeholder="Yeni şifre"
              value={resetting.password} onChange={e => setResetting({ ...resetting, password: e.target.value })}
              className="picker-search" style={{ marginBottom: 16 }} />
            <div className="settings-actions">
              <button className="outline-button" onClick={() => setResetting(null)}>Vazgeç</button>
              <button className="primary-button" onClick={resetPass} data-testid="reset-pass-submit">
                <Save size={16} /> Değiştir
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
